import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "components/atoms";
import { EmailInput } from "components/molecules";
import { useAuth } from "contexts";
import type { UserType } from "types";
import { useContentSanitizer } from "hooks";
import {
  getSortedDestinations,
  getSortedProgramLevels,
  getSortedStudyFields,
} from "services";
import { fetchAuthSession } from "aws-amplify/auth";
import jsonData from "assets/json/Tags.json";
import rightpath from "assets/images/rightpath.svg";
import "./Onboarding.scss";

interface FormData {
  programLevel: string;
  studyFields: string;
  destination: string;
  email: string;
}

interface FormStep {
  id: keyof FormData | "choice";
  title: string;
  type: "options" | "email" | "choice";
  options?: Array<{ value: string; label: string; isDisabled: boolean }>;
  content: {
    title: string;
    description: string;
  };
}

const disabledProgramLevels: string[] = [];
const disabledStudyFields: string[] = [];
const disabledDestinations: string[] = [
  "canada",
  "china",
  "germany",
  "japan",
  "malaysia",
  "new zealand",
  "singapore",
  "south korea",
  "uk",
  "usa",
];

const emailValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
});

const formSteps: FormStep[] = [
  {
    id: "programLevel",
    title: "I want to pursue",
    type: "options",
    options: getSortedProgramLevels(jsonData).map((level) => ({
      value: level.toLowerCase(),
      label: level,
      isDisabled: disabledProgramLevels.includes(level.toLowerCase()),
    })),
    content: {
      title: "Streamlining Your Overseas Education Experience",
      description:
        "We help you discover and select the right courses that match your goals and qualifications. Let's find your perfect educational path.",
    },
  },
  {
    id: "studyFields",
    title: "My favorite subject is",
    type: "options",
    options: getSortedStudyFields(jsonData).map((field) => ({
      value: field.toLowerCase(),
      label: field,
      isDisabled: disabledStudyFields.includes(field.toLowerCase()),
    })),
    content: {
      title: "What interests you?",
      description:
        "Your subject choice shapes your future. Let's find programs that match your academic interests and career goals.",
    },
  },
  {
    id: "destination",
    title: "I plan to study in",
    type: "options",
    options: getSortedDestinations(jsonData).map((dest) => ({
      value: dest.toLowerCase(),
      label: dest,
      isDisabled: disabledDestinations.includes(dest.toLowerCase()),
    })),
    content: {
      title: "Choose Your Study Destination",
      description:
        "From top universities to vibrant cities, explore destinations that offer the perfect environment for your studies.",
    },
  },
  {
    id: "email",
    title: "",
    type: "email",
    content: {
      title: "Almost There!",
      description:
        "Let's get started on making your study abroad dreams a reality.",
    },
  },
  {
    id: "choice",
    title: "",
    type: "choice",
    content: {
      title: "Ready When You Are",
      description: "Your path to overseas education starts here.",
    },
  },
];

const Onboarding: React.FC = () => {
  const { authState, setAuthState } = useAuth();
  const navigate = useNavigate();
  const { sanitizeObject } = useContentSanitizer();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    programLevel: "",
    studyFields: "",
    destination: "",
    email: "",
  });

  const updateEmailFormData = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      email,
    }));
  };

  const updateFieldData = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getIsStepValid = () => {
    const currentField = formSteps[currentStep].id;
    return (
      currentField === "choice" ||
      Boolean(formData[currentField as keyof FormData])
    );
  };

  const canIncrementStep = () => currentStep < formSteps.length - 2;

  const emailFormik = useFormik({
    initialValues: {
      email: formData.email,
    },
    validationSchema: emailValidationSchema,
    /* coverage-ignore-next */
    onSubmit: () => {},
  });

  const handleSelect = (value: string): void => {
    const currentField = formSteps[currentStep].id;
    currentField !== "choice" &&
      updateFieldData(currentField as keyof FormData, value);
  };

  useEffect(() => {
    updateEmailFormData(emailFormik.values.email);
  }, [emailFormik.values.email]);

  const handleNext = (): void => {
    canIncrementStep() && setCurrentStep((prev) => prev + 1);
  };

  const handlePreScreening = () => {
    const sanitizedData = sanitizeObject(formData);
    sessionStorage.setItem("onboardingData", JSON.stringify(sanitizedData));
    navigate("/preScreening", {
      state: {
        formData: {
          programLevel: sanitizedData.programLevel,
          studyFields: sanitizedData.studyFields,
          destination: sanitizedData.destination,
        },
      },
    });
  };

  const handleSignUp = async () => {
    const session = await fetchAuthSession();
    const isAuthenticated = !!session.tokens?.idToken;

    const sanitizedData = sanitizeObject(formData);
    sessionStorage.setItem("onboardingData", JSON.stringify(sanitizedData));

    setAuthState((prevState) => ({
      ...prevState,
      userType: "STUDENT" as UserType,
      nextStep: "SIGN_UP",
    }));

    if (isAuthenticated) {
      navigate("/preScreening", {
        state: {
          email: sanitizedData.email,
          redirectAfterSignUp: "/preScreening",
          formData: {
            programLevel: sanitizedData.programLevel,
            studyFields: sanitizedData.studyFields,
            destination: sanitizedData.destination,
          },
        },
      });
    } else {
      navigate("/authenticate", {
        state: {
          email: sanitizedData.email,
          redirectAfterSignUp: "/preScreening",
          formData: {
            programLevel: sanitizedData.programLevel,
            studyFields: sanitizedData.studyFields,
            destination: sanitizedData.destination,
          },
        },
      });
    }
  };

  const handleSkip = () => {
    const sanitizedData = sanitizeObject(formData);
    sessionStorage.setItem("onboardingData", JSON.stringify(sanitizedData));

    navigate("/search", {
      state: {
        programLevel: sanitizedData.programLevel,
        studyFields: sanitizedData.studyFields,
        destination: sanitizedData.destination,
      },
    });
  };

  const renderFormField = () => {
    const currentFieldData = formSteps[currentStep];

    const renderEmailStep = () =>
      authState.isSignedIn ? (
        <div className="authenticated-user-content">
          <h2 className="step-title">
            Ready for your personalized assessment?
          </h2>
          <p className="help-text">
            Let's help you find the perfect educational path for your future.
          </p>
          <Button
            text="Start Pre-screening Assessment"
            type="button"
            className="primary-button"
            onClick={handlePreScreening}
            variant="primary"
            width="100%"
          />
        </div>
      ) : (
        <div className="email-step-content">
          <h4 className="step-title">
            Sign up now for your free pre-screening assessment
          </h4>
          <EmailInput
            id="email"
            label=""
            getFieldProps={emailFormik.getFieldProps}
            touched={emailFormik.touched.email}
            error={emailFormik.errors.email}
          />
          <div className="onboarding-button-group">
            <Button
              text="Sign Up"
              type="button"
              className="primary-button"
              onClick={handleSignUp}
              variant="primary"
              width="100%"
              disabled={!emailFormik.isValid || !emailFormik.values.email}
            />
            <Button
              text="Continue without signup"
              type="button"
              className="secondary-button"
              onClick={handleSkip}
              variant="borderless"
              width="100%"
            />
          </div>
        </div>
      );

    const renderOptionsGrid = () => (
      <div className="options-grid">
        {currentFieldData.options?.map((option) => (
          <div
            key={option.value}
            className={`option-item ${
              formData[currentFieldData.id as keyof FormData] === option.value
                ? "selected"
                : ""
            } ${option.isDisabled ? "disabled" : ""}`}
            onClick={() => {
              if (!option.isDisabled) {
                handleSelect(option.value);
              }
            }}
          >
            {option.label}
          </div>
        ))}
      </div>
    );

    return currentFieldData.type === "email"
      ? renderEmailStep()
      : currentFieldData.options
        ? renderOptionsGrid()
        : null;
  };

  return (
    <section>
      <div className="container-fluid assessment-container">
        <div className="container assessment-content py-5">
          <img src={rightpath} alt="rightpath" className="rightpathbg" />
          <div className="row p-2">
            <div className="col-md-6 content-column">
              <div className="content-wrapper">
                <h2 className="main-title">
                  {formSteps[currentStep].content.title}
                </h2>
                <p className="main-description">
                  {formSteps[currentStep].content.description}
                </p>
              </div>
            </div>

            <div className="col-md-6 form-column">
              <div className="form-wrapper">
                <h3 className="form-title">
                  Get Your <span className="underlined">Free Assessment</span>
                </h3>
                <div className="form-step">
                  {currentStep < formSteps.length - 2 && (
                    <h4 className="step-title">
                      {formSteps[currentStep].title}
                    </h4>
                  )}
                  {renderFormField()}
                  {currentStep < formSteps.length - 2 && (
                    <Button
                      type="button"
                      text="Next"
                      className="next-button"
                      onClick={handleNext}
                      disabled={!getIsStepValid()}
                      width="100%"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Onboarding;
